import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import InfiniteScroll from 'react-infinite-scroller'
import {
  setActiveModal,
  fetchCategorizedRegItems,
  fetchFeedSession,
  fetchLatestFeedSession,
} from 'registry/actions'
import {
  getArrivalDate,
  getFeedItems,
  getFeedSessionNumber,
  getIsFetchingFeed,
  getHasMoreFeedItems,
  getIsMobile,
  getBabylistAppInstalled,
  getUserOwnsRegistry,
  getRegistryOwnerId,
  getRegistryId,
} from 'registry/reducers'
import { AppPromoTile } from 'registry/home/components/feed-items/app-promo-tile'
import { tracking, track } from 'lib/analytics'
import { DEFAULT_FEED_ITEMS_PER_PAGE } from 'shared/constants'
import FeedItemLayout from './feed-item-layout'
import FeedItemGhostList from './feed-item-ghost'
import FeedItemAdminWrapper from './FeedItemAdminWrapper/FeedItemAdminWrapper'

const FEED_ITEMS_PER_PAGE = DEFAULT_FEED_ITEMS_PER_PAGE
const APP_PROMO_TILE_INDEX = 20

const AppPromoTileFeedItem = () => (
  <div className="mbxl">
    <AppPromoTile />
  </div>
)

const InjectAppPromoTile = ({ children, hasMore }) => {
  let newChildren = children

  // approximate inserting app promo around the 20th item, otherwise chuck on the end
  if (hasMore && children.length >= APP_PROMO_TILE_INDEX) {
    newChildren = [...children]
    newChildren.splice(
      APP_PROMO_TILE_INDEX,
      0,
      <AppPromoTileFeedItem key="promo-inject" />
    )
  } else if (!hasMore && children.length < APP_PROMO_TILE_INDEX) {
    newChildren = [...children]
    newChildren.push(<AppPromoTileFeedItem key="promo-push" />)
  } else {
    // don't inject
  }

  return newChildren
}

@tracking({ eventLocation: track.EventLocation.FEED })
class FeedList extends PureComponent {
  static propTypes = {
    feedItems: PropTypes.array,
    hasMoreFeedItems: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isFetchingFeed: PropTypes.bool.isRequired,
    isRegistryOwner: PropTypes.bool,
    registryOwnerId: PropTypes.number,
    registryId: PropTypes.number,
  }

  static defaultProps = {
    feedItems: [],
  }

  state = {
    nextPage: 2,
  }

  componentDidMount() {
    // FeedList is calling `fetchLatestFeedSession` twice because it gets re-mounted when switching from StaticRouter to BrowserRouter
    if (!this.props.currentFeedSession && !this.props.isFetchingFeed) {
      this.fetchLatestFeed()
    }
  }

  componentDidUpdate(prevProps) {
    // if the arrival date changes we need to refresh the feed
    if (this.props.arrivalDate !== prevProps.arrivalDate) {
      this.fetchLatestFeed()
    }
  }

  fetchLatestFeed = () =>
    this.props.fetchLatestFeedSession({
      per_page: FEED_ITEMS_PER_PAGE,
      log_view: true,
    })

  loadItems = () => {
    if (!this.props.isFetchingFeed) {
      this.props
        .fetchFeedSession({
          page: this.state.nextPage,
          per_page: FEED_ITEMS_PER_PAGE,
        })
        .then(() =>
          this.setState({
            nextPage: this.state.nextPage + 1,
          })
        )
    }
  }

  render() {
    const {
      currentFeedSession,
      feedItems,
      isAdmin,
      isMobile,
      installedBabylistApp,
      isRegistryOwner,
      registryOwnerId,
      registryId,
    } = this.props
    let items = []

    feedItems.map((props) => {
      const { tracking, ...rest } = props
      // IMPORTANT: when adding values to tracking props, ensure you do not create a new object
      // as this will cause the 'react-on-screen' to think the payload needs to be fired again.
      // Apparently it uses object reference instead of deep comparison to determine if the payload has fired.
      tracking.registryId = registryId
      tracking.registryOwnerId = registryOwnerId
      tracking.isRegistryOwner = isRegistryOwner

      let feedItem = (
        <FeedItemLayout {...rest} key={props.id} trackingProps={tracking} />
      )

      if (isAdmin) {
        feedItem = (
          <FeedItemAdminWrapper
            contentfulId={props.id}
            contentfulType={props.type}
            key={props.id}
          >
            {feedItem}
          </FeedItemAdminWrapper>
        )
      }

      items.push(feedItem)
    })

    if (!currentFeedSession) {
      return <FeedItemGhostList className="ptl" />
    }

    /**
     * Render regular feed items if on desktop.
     */
    let Items = items
    if (isMobile && !installedBabylistApp)
      Items = (
        <InjectAppPromoTile hasMore={this.props.hasMoreFeedItems}>
          {items}
        </InjectAppPromoTile>
      )

    return (
      <InfiniteScroll
        initialLoad={false}
        loadMore={this.loadItems.bind(this)}
        hasMore={this.props.hasMoreFeedItems}
        loader={<FeedItemGhostList length={1} />}
      >
        <div className="animated fadeIn pvl feed-list-items">{Items}</div>
      </InfiniteScroll>
    )
  }
}

const mapStateToProps = (state) => {
  return {
    currentFeedSession: getFeedSessionNumber(state),
    feedItems: getFeedItems(state),
    hasMoreFeedItems: getHasMoreFeedItems(state),
    isFetchingFeed: getIsFetchingFeed(state),
    isMobile: getIsMobile(state),
    installedBabylistApp: getBabylistAppInstalled(state),
    isRegistryOwner: getUserOwnsRegistry(state),
    registryOwnerId: getRegistryOwnerId(state),
    registryId: getRegistryId(state),
    arrivalDate: getArrivalDate(state),
  }
}

export default connect(mapStateToProps, {
  setActiveModal,
  fetchCategorizedRegItems,
  fetchFeedSession,
  fetchLatestFeedSession,
})(FeedList)
